/**
 * Timed words from lrclib, browser side (Lesson Dashboard pass 4 S1,
 * 2026-10-02). When a song is added its words are fetched WITH their timing,
 * so a lyric video can be followed before any track exists.
 *
 * parseLrc / words / titleQueries are copies of scripts/music/cutLyricsModel.js
 * (CRA cannot import outside src/); lrcModel.test.js runs both on the same
 * inputs, so the two cannot drift silently.
 *
 * lrclib's synced lines are timed to ONE recording (usually the original).
 * Nothing here can hear the recording, so a song timed this way is stored as
 * `checked: false`; the studio Mac checks it against the lyric video's audio
 * later (card "Lyrics: time them from the lyric video…").
 */

/** "[mm:ss.xx] words" lines → [{ t: seconds, text }], blanks dropped, sorted. */
export const parseLrc = (synced) => {
	const out = [];
	for (const raw of String(synced || '').split(/\r?\n/)) {
		const m = /^\s*\[(\d+):(\d+(?:\.\d+)?)\]\s*(.*)$/.exec(raw);
		if (!m) continue;
		const t = parseInt(m[1], 10) * 60 + parseFloat(m[2]);
		const text = m[3].trim();
		if (text) out.push({ t, text });
	}
	return out.sort((a, b) => a.t - b.t);
};

export const words = (s) =>
	String(s || '')
		.toLowerCase()
		.replace(/[’']/g, '')
		.replace(/[^a-z0-9\s]/g, ' ')
		.split(/\s+/)
		.filter(Boolean);

/** Title forms worth searching, most specific first. */
export const titleQueries = (title) => {
	const base = String(title || '').replace(/\s+/g, ' ').trim();
	if (!base) return [];
	const clean = base
		.replace(/\s*[([][^)\]]*[)\]]\s*/g, ' ')
		.replace(/\s+-\s+(?:\d{4}\s+)?(?:remaster\w*|live|from|version|radio|single|mono|stereo|edit|original)\b.*$/i, '')
		.replace(/\s+(?:feat\.?|ft\.?|featuring)\s.*$/i, '')
		.replace(/\s+/g, ' ')
		.trim();
	const out = [clean, base];
	const letters = clean.replace(/[.\-\s]/g, '');
	if (/^([A-Za-z][.\-]\s?){2,}[A-Za-z]\.?$/.test(clean)) out.push(letters);
	return [...new Set(out.filter(Boolean))];
};

const normTitle = (s) => words(titleQueries(s)[0] || '').join(' ');

/** Synced lines a recording could actually hold: at least four, and the last
 *  one sung before the recording ends. lrclib has hits whose lines run past
 *  their own length (seen 2026-10-02: lines to 7:18 on a 71 s "recording"),
 *  which would highlight nothing for most of a song. */
const plausible = (h) => {
	const t = parseLrc(h.syncedLyrics);
	if (t.length < 4) return false;
	return !(Number.isFinite(h.duration) && h.duration > 0 && t[t.length - 1].t > h.duration + 5);
};

/**
 * The synced hit to trust for a song, or null. A hit must have the SAME
 * title (a same-titled search can return anything that merely contains the
 * words); among those, one whose artist shares a word with the song's
 * artist wins, then lrclib's order. With no synced same-title hit the
 * caller falls back to plain words.
 */
export const pickSyncedHit = (hits, { title = '', artist = '', exclude = [] } = {}) => {
	const want = normTitle(title);
	if (!want) return null;
	const aw = words(artist);
	const ranked = (Array.isArray(hits) ? hits : [])
		.map((h, i) => ({ h, i }))
		.filter(({ h }) => h && !exclude.includes(h.id))
		.filter(({ h }) => h && h.syncedLyrics && normTitle(h.trackName || h.name || '') === want && plausible(h))
		.map(({ h, i }) => {
			const hw = new Set(words(h.artistName || ''));
			return { h, i, s: aw.length && aw.some((w) => hw.has(w)) ? 1 : 0 };
		})
		.sort((a, b) => b.s - a.s || a.i - b.i);
	if (!ranked.length) return null;
	// With an artist known, a hit by someone else is a different song.
	if (aw.length && ranked[0].s === 0) return null;
	return ranked[0].h;
};

/** The `lyrics` value for a song timed from an lrclib hit. */
export const timedLyricsFromHit = (hit) => {
	const timed = parseLrc(hit.syncedLyrics);
	const text = String(hit.plainLyrics || '').trim() || timed.map((l) => l.text).join('\n');
	return {
		text,
		source: 'LRCLIB',
		timed,
		timing: {
			method: 'synced',
			from: 'lrclib',
			lrclibId: hit.id || null,
			durationSec: Number.isFinite(hit.duration) ? hit.duration : null,
			checked: false,
		},
	};
};
